import {DestroyRef, inject, Injectable, InjectionToken} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {EMPTY, Observable} from 'rxjs';
import {LangDefinition, TranslocoService} from '@jsverse/transloco';
import {TranslocoLocaleService} from '@jsverse/transloco-locale';
import {CldrIntlService, IntlService} from '@progress/kendo-angular-intl';

//Clé du choix de langue mémorisé dans le navigateur
const cleStockageLangue = "ammon_langue";

//Langue de repli quand ni le choix mémorisé, ni le claim, ni le navigateur ne sont supportés
const langueRepli = "en";

//Délai au-delà duquel la lecture de la langue de l’utilisateur est abandonnée, en millisecondes
const delaiLectureMax = 5000;

//Libellés des langues, dans leur propre langue : ils ne se traduisent pas
const libellesLangues: Record<string, string> =
{
  fr: "Français",
  en: "English",
  nl: "Nederlands",
  it: "Italiano",
  es: "Español",
  de: "Deutsch"
};

//Locale courante, lisible hors injection (formatage de la grille)
export const langueCourante = {locale: "fr-FR"};

//Stockage serveur de la langue choisie par l’utilisateur, qui la retrouve d’un poste et d’un navigateur à l’autre.
//Fourni par l’application (préférences Ammon Services) : cette librairie ne connaît pas l’API.
//Sans lui, le choix reste mémorisé par navigateur
export interface StockageLangueUtilisateur
{
  //Retourne la langue enregistrée pour l’utilisateur, null s’il n’en a pas.
  //Lève une erreur si le stockage ne répond pas
  Lire () : Promise<string | null>;

  //Enregistre la langue choisie par l’utilisateur. Lève une erreur en cas d’échec
  //  Paramètres
  //    langue : Code de la langue
  Enregistrer (langue: string) : Promise<void>;
}

//Jeton du stockage serveur de la langue de l’utilisateur
export const VAL_STOCKAGE_LANGUE_UTILISATEUR = new InjectionToken<StockageLangueUtilisateur> ("VAL_STOCKAGE_LANGUE_UTILISATEUR");

//Injecte un service, ou retourne null s’il n’est pas disponible dans l’application
//  Paramètres
//    injecter : Fonction d’injection, appelée dans le contexte d’injection
function InjecterSiDisponible<T> (injecter: () => T) : T | null
{
  try { return injecter (); }
  catch { return null; }
}

//Service de gestion de la langue de l’interface
@Injectable ({providedIn: 'root'})
export class ValLangueService
{
  //Ces services sont providedIn root mais échouent à la création quand l’application ne les configure pas
  private translocoService = InjecterSiDisponible (() => inject (TranslocoService));
  private translocoLocaleService = InjecterSiDisponible (() => inject (TranslocoLocaleService));
  private intlService = InjecterSiDisponible (() => inject (IntlService));
  private stockageLangueUtilisateur = inject (VAL_STOCKAGE_LANGUE_UTILISATEUR, {optional: true});

  //Création
  constructor ()
  {
    //La locale suit la langue par le mapping de provideTranslocoLocale. Une application qui ne le
    //configure pas garde ses formats : ni Kendo ni la grille ne sont touchés
    this.translocoLocaleService?.localeChanges$
      .pipe (takeUntilDestroyed (inject (DestroyRef)))
      .subscribe (locale => this.AppliquerLocale (locale));
  }

  //Langues disponibles dans l’application
  public get Langues () : string[]
  {
    if (!this.translocoService)
      return [];

    return this.translocoService.getAvailableLangs ().map (l => typeof l === "string" ? l : (l as LangDefinition).id);
  }

  //Langue active
  public get LangueActive () : string
  {
    return this.translocoService?.getActiveLang () ?? "";
  }

  //Retourne le libellé d’une langue, dans sa propre langue
  //  Paramètres
  //    langue : Code de la langue
  public Libelle (langue: string) : string
  {
    return libellesLangues[langue] ?? langue;
  }

  //Émet à chaque fois que les traductions de la langue active sont chargées (ouverture, changement de langue)
  public get TraductionsChargees$ () : Observable<unknown>
  {
    return this.translocoService?.selectTranslation () ?? EMPTY;
  }

  //Traduit une clé dans la langue active, ou retourne le texte par défaut si l’application ne la porte pas.
  //Sert aux composants partagés, utilisés aussi par des applications sans Transloco ou sans ces clés :
  //elles gardent leur texte d’origine. Le texte est rendu tel quel, sans interpolation.
  //  Paramètres
  //    cle    : Clé de traduction, depuis la racine
  //    defaut : Texte affiché quand la clé est absente
  public TraduireOuDefaut (cle: string, defaut: string) : string
  {
    let traduction: unknown;

    if (!this.translocoService)
      return defaut;

    traduction = this.translocoService.getTranslation (this.translocoService.getActiveLang ())?.[cle];

    return typeof traduction === "string" && traduction !== "" ? traduction : defaut;
  }

  //Traduit un libellé venu de la base, ou le retourne tel quel. La langue par défaut garde toujours le
  //libellé de la base : c’est lui qui porte une éventuelle surcharge client (DICO_CLIENT), que le front
  //ne sait pas distinguer d’un libellé standard. Les autres langues prennent la clé si elle existe.
  //  Paramètres
  //    cle     : Clé de traduction, depuis la racine
  //    libelle : Libellé de la base
  public TraduireLibelleBase (cle: string, libelle: string) : string
  {
    if (!this.translocoService || this.LangueActive === this.translocoService.getDefaultLang ())
      return libelle;

    return this.TraduireOuDefaut (cle, libelle);
  }

  //Choisit la langue à l’ouverture : choix mémorisé, puis claim de l’utilisateur, puis navigateur,
  //puis anglais si aucune n’est supportée. Avec un stockage serveur, le choix mémorisé n’en est que
  //la copie : ChargerLangueUtilisateur le remet à jour une fois l’utilisateur connecté
  //  Paramètres
  //    langueUtilisateur : Langue portée par le claim locale de l’utilisateur
  public Initialiser (langueUtilisateur?: string | null) : void
  {
    if (!this.translocoService)
      return;

    if (!this.Appliquer (localStorage.getItem (cleStockageLangue)))
      this.AppliquerDefaut (langueUtilisateur);
  }

  //Applique la langue enregistrée pour l’utilisateur, qui prime sur la copie du navigateur.
  //Sans langue enregistrée, la copie est oubliée et la langue revient au défaut : elle a pu être
  //réinitialisée depuis un autre poste. Sans réponse du stockage, en erreur ou au-delà du délai
  //maximal, la langue en place est conservée
  //  Paramètres
  //    langueUtilisateur : Langue portée par le claim locale de l’utilisateur
  //  Retourne
  //    Opération asynchrone, qui ne lève jamais d’erreur
  //  Remarques
  //    Le callback de connexion attend cette lecture avant de naviguer : sans borne, un stockage
  //    qui ne répond pas laisserait l’utilisateur sur la page de callback jusqu’au délai du
  //    navigateur. Une réponse arrivée après coup est ignorée, la langue ne change pas sous l’utilisateur.
  public async ChargerLangueUtilisateur (langueUtilisateur?: string | null) : Promise<void>
  {
    let langue: string | null;
    let minuterie: ReturnType<typeof setTimeout> | undefined;

    if (!this.translocoService || !this.stockageLangueUtilisateur)
      return;

    try
    {
      langue = this.LangueSupportee (await Promise.race ([this.stockageLangueUtilisateur.Lire (),
                                                          new Promise<never> ((_, abandonner) => { minuterie = setTimeout (() => abandonner (new Error ("Délai de lecture dépassé")), delaiLectureMax); })]));
    }
    catch
    {
      return;
    }
    finally
    {
      clearTimeout (minuterie);
    }

    if (langue)
    {
      localStorage.setItem (cleStockageLangue, langue);
      this.translocoService.setActiveLang (langue);
    }
    else
      this.RetablirDefaut (langueUtilisateur);
  }

  //Change la langue de l’interface et mémorise le choix : pour l’utilisateur s’il y a un stockage
  //serveur, dans le navigateur dans tous les cas. Un échec du stockage serveur ne revient pas sur le changement
  //  Paramètres
  //    langue : Code de la langue
  //  Retourne
  //    Vrai si le choix est enregistré pour l’utilisateur, ou s’il n’y a pas de stockage serveur ; ne lève jamais d’erreur
  public async Changer (langue: string) : Promise<boolean>
  {
    if (!this.translocoService?.isLang (langue))
      return false;

    localStorage.setItem (cleStockageLangue, langue);

    this.translocoService.setActiveLang (langue);

    if (!this.stockageLangueUtilisateur)
      return true;

    try
    {
      await this.stockageLangueUtilisateur.Enregistrer (langue);

      return true;
    }
    catch
    {
      return false;
    }
  }

  //Oublie le choix mémorisé dans le navigateur et revient à la langue par défaut : claim, puis navigateur,
  //puis anglais. Le stockage serveur n’est pas touché : sa remise à zéro appartient à l’appelant
  //  Paramètres
  //    langueUtilisateur : Langue portée par le claim locale de l’utilisateur
  public RetablirDefaut (langueUtilisateur?: string | null) : void
  {
    if (!this.translocoService)
      return;

    localStorage.removeItem (cleStockageLangue);

    this.AppliquerDefaut (langueUtilisateur);
  }

  //Applique une langue si elle est supportée
  //  Paramètres
  //    code : Code de langue ou de locale
  //  Retourne
  //    Vrai si la langue a été appliquée
  private Appliquer (code?: string | null) : boolean
  {
    let langue: string | null;

    langue = this.LangueSupportee (code);

    if (langue)
      this.translocoService!.setActiveLang (langue);

    return !!langue;
  }

  //Applique la langue par défaut : claim, puis navigateur, puis anglais
  //  Paramètres
  //    langueUtilisateur : Langue portée par le claim locale de l’utilisateur
  private AppliquerDefaut (langueUtilisateur?: string | null) : void
  {
    if (!this.Appliquer (langueUtilisateur) && !this.Appliquer (navigator.language))
      this.Appliquer (langueRepli);
  }

  //Retourne la langue supportée correspondant à un code (fr, fr-FR, nl-BE…), ou null
  //  Paramètres
  //    code : Code de langue ou de locale
  private LangueSupportee (code?: string | null) : string | null
  {
    let langue: string;

    if (!code || !this.translocoService)
      return null;

    langue = code.toLowerCase ();

    if (this.translocoService.isLang (langue))
      return langue;

    langue = langue.split ("-")[0];

    return this.translocoService.isLang (langue) ? langue : null;
  }

  //Fait suivre la langue active aux formats de dates et de montants
  //  Paramètres
  //    locale : Locale de la langue active (fr-FR, en-GB, nl-NL, it-IT, es-ES, de-DE)
  private AppliquerLocale (locale: string) : void
  {
    if (!locale)
      return;

    langueCourante.locale = locale;

    if (this.intlService instanceof CldrIntlService)
    {
      const localePrecedente = this.intlService.localeId;

      //Une application qui n’a pas chargé les données Kendo de la locale garde la précédente
      try
      {
        this.intlService.localeId = locale;
        this.intlService.formatNumber (1, "n2");
      }
      catch
      {
        this.intlService.localeId = localePrecedente;
      }
    }

    document.documentElement.lang = this.LangueActive;
  }
}
